import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Paper,
  Table,
  Button,
  TextInput,
  Group,
  Badge,
  ActionIcon,
  Modal,
  Stack,
  Alert,
  Loader,
  Pagination,
  Select,
  Box,
  Grid,
  Divider,
  Tabs,
  NumberInput,
  Checkbox
} from '@mantine/core';
import { 
  IconSearch, 
  IconEdit, 
  IconTrash, 
  IconEye, 
  IconPlus,
  IconCalculator,
  IconReport,
  IconSettings,
  IconReceipt,
  IconCreditCard
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { API_URL } from '../../../utils/constants';

function TransactionsScreen() {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('ledger_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [editingTransaction, setEditingTransaction] = useState({
    ledger_name: '',
    code: '',
    description: '',
    bucket: 'Revenue',
    gl_account_id: '',
    tax_code: '',
    amount: '',
    active: true
  });
  const [ledgers, setLedgers] = useState([]);
  const [glAccounts, setGLAccounts] = useState([]);
  const [taxCodes, setTaxCodes] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const bucketOptions = ['Revenue', 'Other Charge 1', 'Other Charge 2', 'Other Charge 3', 'Other Charge 4', 'Payment'];

  // Check authentication on component mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    document.title = 'Accounting - Transactions - Resort Systems';
    console.log('TransactionsScreen component mounted');
  }, [navigate]);

  useEffect(() => {
    fetchTransactions();
    fetchDropdownData();
  }, [searchTerm, sortBy, sortOrder]);

  // Handle URL mode parameter for automatic modal opening
  useEffect(() => {
    const mode = searchParams.get('mode');
    if (mode === 'add') {
      setShowAddModal(true);
    } else if (mode === 'change' && selectedTransaction) {
      setShowEditModal(true);
    } else if (mode === 'delete' && selectedTransaction) {
      setShowDeleteModal(true);
    }
  }, [searchParams, selectedTransaction]);

  const fetchDropdownData = async () => {
    try {
      // Fetch ledgers
      const ledgersResponse = await fetch(`${API_URL}/ledgers`);
      if (ledgersResponse.ok) {
        const ledgersData = await ledgersResponse.json();
        setLedgers(ledgersData);
      }

      // Fetch GL accounts
      const glAccountsResponse = await fetch(`${API_URL}/gl-accounts-dropdown`);
      if (glAccountsResponse.ok) {
        const glAccountsData = await glAccountsResponse.json();
        setGLAccounts(glAccountsData);
      }

      // Fetch tax codes
      const taxCodesResponse = await fetch(`${API_URL}/tax-codes-dropdown`);
      if (taxCodesResponse.ok) {
        const taxCodesData = await taxCodesResponse.json();
        setTaxCodes(taxCodesData);
      }
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  };

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const params = new URLSearchParams({
        sortBy,
        sortOrder
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await fetch(`${API_URL}/transactions?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTransactions(data);
      } else {
        if (response.status === 401) {
          navigate('/login');
          return;
        }
        const errorData = await response.json();
        setError(errorData.message || 'Error fetching transactions');
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setError('Error fetching transactions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTransactions();
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleAdd = () => {
    setEditingTransaction({
      ledger_name: '',
      code: '',
      description: '',
      bucket: 'Revenue',
      gl_account_id: '',
      tax_code: '',
      amount: '',
      active: true
    });
    setShowAddModal(true);
    setSearchParams({ mode: 'add' });
  };

  const handleEdit = (transaction) => {
    setEditingTransaction({ ...transaction });
    setShowEditModal(true);
    setSearchParams({ mode: 'change' });
  };

  const handleDelete = (transaction) => {
    setSelectedTransaction(transaction);
    setShowDeleteModal(true);
    setSearchParams({ mode: 'delete' });
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_URL}/transactions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...editingTransaction,
          amount: parseFloat(editingTransaction.amount),
          gl_account_id: parseInt(editingTransaction.gl_account_id)
        }),
      });

      if (response.ok) {
        setShowAddModal(false);
        setSearchParams({ mode: 'list' });
        fetchTransactions();
        setError('');
      } else {
        if (response.status === 401) {
          navigate('/login');
          return;
        }
        const errorData = await response.json();
        setError(errorData.message || 'Error creating transaction');
      }
    } catch (error) {
      console.error('Error creating transaction:', error);
      setError('Error creating transaction. Please try again.');
    }
  };

  const handleUpdate = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_URL}/transactions/${editingTransaction.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...editingTransaction,
          amount: parseFloat(editingTransaction.amount),
          gl_account_id: parseInt(editingTransaction.gl_account_id)
        }),
      });

      if (response.ok) {
        setShowEditModal(false);
        setSearchParams({ mode: 'list' });
        fetchTransactions();
        setError('');
      } else {
        if (response.status === 401) {
          navigate('/login');
          return;
        }
        const errorData = await response.json();
        setError(errorData.message || 'Error updating transaction');
      }
    } catch (error) {
      console.error('Error updating transaction:', error);
      setError('Error updating transaction. Please try again.');
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_URL}/transactions/${selectedTransaction.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setShowDeleteModal(false);
        setSearchParams({ mode: 'list' });
        fetchTransactions();
        setSelectedTransaction(null);
        setError('');
      } else {
        if (response.status === 401) {
          navigate('/login');
          return;
        }
        const errorData = await response.json();
        setError(errorData.message || 'Error deleting transaction');
      }
    } catch (error) {
      console.error('Error deleting transaction:', error);
      setError('Error deleting transaction. Please try again.');
    }
  };

  const handleInputChange = (field, value) => {
    setEditingTransaction(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRowClick = (transaction) => {
    setSelectedTransaction(transaction);
  };

  const getSortIcon = (field) => {
    if (sortBy === field) {
      return sortOrder === 'asc' ? ' ↑' : ' ↓';
    }
    return '';
  };

  const getBucketBadge = (bucket) => {
    const colorMap = {
      'Revenue': 'green',
      'Other Charge 1': 'yellow',
      'Other Charge 2': 'blue',
      'Other Charge 3': 'gray',
      'Other Charge 4': 'dark',
      'Payment': 'violet'
    };
    return <Badge color={colorMap[bucket] || 'gray'}>{bucket}</Badge>;
  };

  const getActiveBadge = (active) => {
    return active ? <Badge color="green">Active</Badge> : <Badge color="red">Inactive</Badge>;
  };

  if (loading) {
    return (
      <Container size="lg" py="xl">
        <Group justify="center">
          <Loader size="lg" />
        </Group>
      </Container>
    );
  }

  return (
    <Container fluid p={0} style={{ height: '100vh' }}>
      <Grid gutter={0} style={{ height: '100%' }}>
        {/* Side Menu */}
        <Grid.Col span={1.2} style={{ backgroundColor: 'var(--mantine-color-gray-0)', padding: '0.5rem', borderRight: '1px solid var(--mantine-color-gray-2)' }}>
          <Stack gap="xs">
            <Title order={6} c="dark" mb="xs" ta="center">Transactions</Title>
            
            {/* Primary Actions */}
            <Button 
              variant="subtle" 
              color="blue"
              fullWidth
              size="sm"
              onClick={() => setSearchTerm('')}
              leftSection={<IconSearch size="0.8rem" />}
            >
              Find
            </Button>
            <Button 
              variant="subtle" 
              color="green"
              fullWidth
              size="sm"
              onClick={handleAdd}
              leftSection={<IconPlus size="0.8rem" />}
            >
              Add
            </Button>
            <Button 
              variant="subtle" 
              color="orange"
              fullWidth
              size="sm"
              onClick={() => selectedTransaction && handleEdit(selectedTransaction)}
              disabled={!selectedTransaction}
              leftSection={<IconEdit size="0.8rem" />}
            >
              Change
            </Button>
            <Button 
              variant="subtle" 
              color="red"
              fullWidth
              size="sm"
              onClick={() => selectedTransaction && handleDelete(selectedTransaction)}
              disabled={!selectedTransaction}
              leftSection={<IconTrash size="0.8rem" />}
            >
              Delete
            </Button>
            
            <Divider color="gray.2" my="sm" />
            
            {/* Transaction Specific Actions */}
            <Button 
              variant="subtle" 
              color="violet"
              fullWidth
              size="sm"
              leftSection={<IconReceipt size="0.8rem" />}
            >
              Post Transaction
            </Button>
            <Button 
              variant="subtle" 
              color="indigo"
              fullWidth
              size="sm"
              leftSection={<IconCalculator size="0.8rem" />}
            >
              Trial Balance
            </Button>
            
            <Divider color="gray.2" my="sm" />
            
            {/* Reports and Settings */}
            <Button 
              variant="subtle" 
              color="grape"
              fullWidth
              size="sm"
              leftSection={<IconReport size="0.8rem" />}
            >
              Reports
            </Button>
            <Button 
              variant="subtle" 
              color="dark"
              fullWidth
              size="sm"
              leftSection={<IconSettings size="0.8rem" />}
            >
              Settings
            </Button>
          </Stack>
        </Grid.Col>

        {/* Main Content */}
        <Grid.Col span={10.8} style={{ padding: '1rem' }}>
          <Stack gap="lg">
            <Group justify="space-between">
              <Title order={1}>Transactions List</Title>
            </Group>

            {error && (
              <Alert color="red" title="Error">
                {error}
              </Alert>
            )}

            {/* Search Bar */}
            <Paper p="md" withBorder>
              <Group>
                <TextInput
                  placeholder="Search transactions by ledger, code, description, or GL account..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  leftSection={<IconSearch size="1rem" />}
                  style={{ flex: 1 }}
                />
                <Button onClick={handleSearch} variant="filled">
                  Search
                </Button>
                <Button 
                  onClick={() => setSearchTerm('')} 
                  disabled={!searchTerm} 
                  variant="outline"
                >
                  Clear
                </Button>
              </Group>
            </Paper>

            {/* Transactions Table */}
            <Paper withBorder>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th 
                      onClick={() => handleSort('ledger_name')} 
                      style={{ cursor: 'pointer' }}
                    >
                      Ledger {getSortIcon('ledger_name')}
                    </Table.Th>
                    <Table.Th 
                      onClick={() => handleSort('code')} 
                      style={{ cursor: 'pointer' }}
                    >
                      Code {getSortIcon('code')}
                    </Table.Th>
                    <Table.Th 
                      onClick={() => handleSort('description')} 
                      style={{ cursor: 'pointer' }}
                    >
                      Description {getSortIcon('description')}
                    </Table.Th>
                    <Table.Th 
                      onClick={() => handleSort('bucket')} 
                      style={{ cursor: 'pointer' }}
                    >
                      Bucket {getSortIcon('bucket')}
                    </Table.Th>
                    <Table.Th>GL Account</Table.Th>
                    <Table.Th>Tax Code</Table.Th>
                    <Table.Th 
                      onClick={() => handleSort('amount')} 
                      style={{ cursor: 'pointer' }}
                    >
                      Amount {getSortIcon('amount')}
                    </Table.Th>
                    <Table.Th 
                      onClick={() => handleSort('active')} 
                      style={{ cursor: 'pointer' }}
                    >
                      Status {getSortIcon('active')}
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {transactions.map((transaction) => (
                    <Table.Tr 
                      key={transaction.id} 
                      onClick={() => handleRowClick(transaction)}
                      style={{ 
                        cursor: 'pointer',
                        backgroundColor: selectedTransaction && selectedTransaction.id === transaction.id ? 'var(--mantine-color-yellow-0)' : '',
                        border: selectedTransaction && selectedTransaction.id === transaction.id ? '2px solid var(--mantine-color-yellow-6)' : ''
                      }}
                    >
                      <Table.Td><Text fw={500}>{transaction.ledger_name}</Text></Table.Td>
                      <Table.Td><Text ff="monospace">{transaction.code}</Text></Table.Td>
                      <Table.Td>{transaction.description}</Table.Td>
                      <Table.Td>{getBucketBadge(transaction.bucket)}</Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {transaction.gl_account_number} - {transaction.gl_account_description}
                        </Text>
                      </Table.Td>
                      <Table.Td>{transaction.tax_code || '-'}</Table.Td>
                      <Table.Td>
                        <Text fw={500} ta="right">
                          ${parseFloat(transaction.amount).toFixed(2)}
                        </Text>
                      </Table.Td>
                      <Table.Td>{getActiveBadge(transaction.active)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              {transactions.length === 0 && (
                <Box p="xl" ta="center">
                  <Text c="dimmed">No transactions found</Text>
                </Box>
              )}
            </Paper>
          </Stack>
        </Grid.Col>
      </Grid>

      {/* Add Transaction Modal */}
      <Modal opened={showAddModal} onClose={() => { setShowAddModal(false); setSearchParams({ mode: 'list' }); }} title="Add New Transaction" size="lg">
        <Stack gap="md">
          <Grid>
            <Grid.Col span={6}>
              <Select
                label="Ledger *"
                value={editingTransaction.ledger_name}
                onChange={(value) => handleInputChange('ledger_name', value)}
                data={ledgers.map(ledger => ({ value: ledger, label: ledger }))}
                placeholder="Select Ledger"
                required
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="Code *"
                value={editingTransaction.code}
                onChange={(e) => handleInputChange('code', e.target.value)}
                placeholder="Enter transaction code"
                required
              />
            </Grid.Col>
          </Grid>

          <TextInput
            label="Description *"
            value={editingTransaction.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Enter transaction description"
            required
          />

          <Grid>
            <Grid.Col span={6}>
              <Select
                label="Bucket *"
                value={editingTransaction.bucket}
                onChange={(value) => handleInputChange('bucket', value)}
                data={bucketOptions.map(bucket => ({ value: bucket, label: bucket }))}
                required
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <Select
                label="GL Account *"
                value={editingTransaction.gl_account_id}
                onChange={(value) => handleInputChange('gl_account_id', value)}
                data={glAccounts.map(account => ({ 
                  value: account.id.toString(), 
                  label: `${account.account_number} - ${account.description}` 
                }))}
                placeholder="Select GL Account"
                required
              />
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={6}>
              <Select
                label="Tax Code"
                value={editingTransaction.tax_code}
                onChange={(value) => handleInputChange('tax_code', value)}
                data={[
                  { value: '', label: 'No Tax' },
                  ...taxCodes.map(tax => ({ 
                    value: tax.transaction_code, 
                    label: `${tax.transaction_code} - ${tax.description}` 
                  }))
                ]}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <NumberInput
                label="Amount *"
                value={editingTransaction.amount}
                onChange={(value) => handleInputChange('amount', value)}
                placeholder="Enter amount"
                decimalScale={2}
                required
              />
            </Grid.Col>
          </Grid>

          <Checkbox
            label="Active"
            checked={editingTransaction.active}
            onChange={(e) => handleInputChange('active', e.target.checked)}
          />
        </Stack>

        <Group justify="flex-end" mt="xl">
          <Button variant="outline" onClick={() => { setShowAddModal(false); setSearchParams({ mode: 'list' }); }}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Transaction
          </Button>
        </Group>
      </Modal>

      {/* Edit Transaction Modal */}
      <Modal opened={showEditModal} onClose={() => { setShowEditModal(false); setSearchParams({ mode: 'list' }); }} title="Edit Transaction" size="lg">
        <Stack gap="md">
          <Grid>
            <Grid.Col span={6}>
              <Select
                label="Ledger *"
                value={editingTransaction.ledger_name}
                onChange={(value) => handleInputChange('ledger_name', value)}
                data={ledgers.map(ledger => ({ value: ledger, label: ledger }))}
                placeholder="Select Ledger"
                required
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="Code *"
                value={editingTransaction.code}
                onChange={(e) => handleInputChange('code', e.target.value)}
                placeholder="Enter transaction code"
                required
              />
            </Grid.Col>
          </Grid>

          <TextInput
            label="Description *"
            value={editingTransaction.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Enter transaction description"
            required
          />

          <Grid>
            <Grid.Col span={6}>
              <Select
                label="Bucket *"
                value={editingTransaction.bucket}
                onChange={(value) => handleInputChange('bucket', value)}
                data={bucketOptions.map(bucket => ({ value: bucket, label: bucket }))}
                required
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <Select
                label="GL Account *"
                value={editingTransaction.gl_account_id}
                onChange={(value) => handleInputChange('gl_account_id', value)}
                data={glAccounts.map(account => ({ 
                  value: account.id.toString(), 
                  label: `${account.account_number} - ${account.description}` 
                }))}
                placeholder="Select GL Account"
                required
              />
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={6}>
              <Select
                label="Tax Code"
                value={editingTransaction.tax_code}
                onChange={(value) => handleInputChange('tax_code', value)}
                data={[
                  { value: '', label: 'No Tax' },
                  ...taxCodes.map(tax => ({ 
                    value: tax.transaction_code, 
                    label: `${tax.transaction_code} - ${tax.description}` 
                  }))
                ]}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <NumberInput
                label="Amount *"
                value={editingTransaction.amount}
                onChange={(value) => handleInputChange('amount', value)}
                placeholder="Enter amount"
                decimalScale={2}
                required
              />
            </Grid.Col>
          </Grid>

          <Checkbox
            label="Active"
            checked={editingTransaction.active}
            onChange={(e) => handleInputChange('active', e.target.checked)}
          />
        </Stack>

        <Group justify="flex-end" mt="xl">
          <Button variant="outline" onClick={() => { setShowEditModal(false); setSearchParams({ mode: 'list' }); }}>
            Cancel
          </Button>
          <Button onClick={handleUpdate}>
            Update Transaction
          </Button>
        </Group>
      </Modal>

      {/* Delete Transaction Modal */}
      <Modal opened={showDeleteModal} onClose={() => { setShowDeleteModal(false); setSearchParams({ mode: 'list' }); }} title="Delete Transaction">
        <Text>
          Are you sure you want to delete the transaction:
        </Text>
        <Text fw={500} mt="sm">
          {selectedTransaction?.ledger_name} - {selectedTransaction?.code}
        </Text>
        <Text fs="italic" c="dimmed" mt="xs">
          {selectedTransaction?.description}
        </Text>
        <Text c="red" mt="md">
          This action cannot be undone.
        </Text>

        <Group justify="flex-end" mt="xl">
          <Button variant="outline" onClick={() => { setShowDeleteModal(false); setSearchParams({ mode: 'list' }); }}>
            Cancel
          </Button>
          <Button color="red" onClick={handleDeleteConfirm}>
            Delete Transaction
          </Button>
        </Group>
      </Modal>

    </Container>
  );
}

export default TransactionsScreen;
